import * as ToastPrimitive from '@radix-ui/react-toast';
import { create } from 'zustand';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@shared/lib/cn';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  tone?: 'success' | 'danger' | 'info';
}

interface ToastStore {
  items: ToastItem[];
  push: (t: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useToasts = create<ToastStore>(set => ({
  items: [],
  push: t => set(s => ({ items: [...s.items, { ...t, id: Math.random().toString(36).slice(2) }] })),
  dismiss: id => set(s => ({ items: s.items.filter(x => x.id !== id) })),
}));

// eslint-disable-next-line react-refresh/only-export-components
export const toast = {
  success: (title: string, description?: string) => useToasts.getState().push({ title, description, tone: 'success' }),
  error: (title: string, description?: string) => useToasts.getState().push({ title, description, tone: 'danger' }),
  info: (title: string, description?: string) => useToasts.getState().push({ title, description, tone: 'info' }),
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const items = useToasts(s => s.items);
  const dismiss = useToasts(s => s.dismiss);

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={4000}>
      {children}
      {items.map(item => {
        const Icon = item.tone === 'success' ? CheckCircle2 : item.tone === 'danger' ? AlertCircle : Info;
        const ring =
          item.tone === 'success' ? 'ring-success/30'
          : item.tone === 'danger' ? 'ring-danger/30'
          : 'ring-info/30';
        const color =
          item.tone === 'success' ? 'text-success'
          : item.tone === 'danger' ? 'text-danger'
          : 'text-info';

        return (
          <ToastPrimitive.Root
            key={item.id}
            onOpenChange={open => { if (!open) dismiss(item.id); }}
            className={cn(
              'group pointer-events-auto flex items-start gap-3 rounded-xl border border-line-subtle/15 bg-bg-2 p-4 shadow-card ring-1',
              ring,
              'data-[state=open]:animate-fade-up data-[state=closed]:animate-fade-in data-[state=closed]:opacity-0'
            )}
          >
            <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', color)} />
            <div className="flex-1 space-y-0.5">
              <ToastPrimitive.Title className="text-sm font-semibold text-fg-hi">{item.title}</ToastPrimitive.Title>
              {item.description ? (
                <ToastPrimitive.Description className="text-xs text-fg-md">{item.description}</ToastPrimitive.Description>
              ) : null}
            </div>
            <ToastPrimitive.Close className="shrink-0 rounded-md p-1 text-fg-lo hover:bg-bg-3 hover:text-fg-hi">
              <X className="h-4 w-4" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        );
      })}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[60] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  );
}
